import { useState } from "react";
import { troubleItems } from "@/mocks/home";

const levelStyles: Record<string, string> = {
  常见: "bg-secondary-100 text-secondary-900",
  少见: "bg-background-200 text-foreground-700",
  需注意: "bg-primary-100 text-primary-700",
};

export default function Troubleshoot() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="troubleshoot" className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs md:text-sm font-label tracking-widest text-primary-600 uppercase">
            Troubleshooting
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">
            打不开怎么办：常见原因与排查
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
            「打不开」其实有很多种不同的表现，原因也各不相同。下面按典型现象分类，点开对应条目即可看到
            可能的原因和处理步骤。建议从第一条开始逐项核对，多数问题在前两步就能定位。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {troubleItems.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.q}
                  className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className="w-full flex items-start gap-3 p-4 text-left cursor-pointer"
                  >
                    <span className="flex-none w-9 h-9 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-700">
                      <i className="ri-error-warning-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span className="flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm md:text-base font-medium text-foreground-900">
                          {item.q}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${levelStyles[item.level] || "bg-secondary-100 text-secondary-900"}`}>
                          {item.level}
                        </span>
                      </span>
                    </span>
                    <i
                      className={`flex-none mt-1 text-foreground-500 transition-transform ${
                        isOpen ? "ri-subtract-line" : "ri-add-line"
                      }`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-5 pl-16">
                      <p className="text-xs md:text-sm text-foreground-600">
                        <span className="font-medium text-foreground-800">可能原因：</span>
                        {item.cause}
                      </p>
                      <ul className="mt-3 space-y-2">
                        {item.solutions.map((solution) => (
                          <li key={solution} className="flex items-start gap-2 text-xs md:text-sm text-foreground-700">
                            <i className="ri-check-line text-accent-600 mt-0.5" aria-hidden="true"></i>
                            <span>{solution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <aside className="lg:col-span-1">
            <div className="p-5 rounded-lg bg-primary-500 text-background-50">
              <div className="w-11 h-11 rounded-md bg-background-50/15 flex items-center justify-center">
                <i className="ri-lightbulb-line text-xl" aria-hidden="true"></i>
              </div>
              <h3 className="mt-4 font-heading text-lg">排查顺序建议</h3>
              <ol className="mt-4 space-y-3 text-xs md:text-sm text-background-50/85">
                <li className="flex gap-2">
                  <span className="font-heading text-accent-300">1.</span>
                  先确认地址是否最新，排除「用了旧地址」这一最普遍的原因。
                </li>
                <li className="flex gap-2">
                  <span className="font-heading text-accent-300">2.</span>
                  再更换网络或清理缓存，排除本地网络与浏览器环境的影响。
                </li>
                <li className="flex gap-2">
                  <span className="font-heading text-accent-300">3.</span>
                  最后切换到备用线路，绕开当前入口本身的拥堵或异常。
                </li>
              </ol>
              <a
                href="#backup"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-50 text-primary-700 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
              >
                <i className="ri-route-line" aria-hidden="true"></i>
                查看备用访问方案
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}